import { LegalLinkKey, LegalLinks } from '../services/legal-links.service';

/** `{key}label{/key}` — a named pair, so one sentence can carry several links. */
const PLACEHOLDER = /\{([a-z_]+)\}([\s\S]*?)\{\/\1\}/g;

/**
 * Escapes the four characters that can end an attribute value or open markup. Both inputs come from
 * the localization database and are editable by translators, so neither is trusted to be markup-free
 * (PR review, N2): a `"` in a URL would otherwise close the `href` and let the rest become
 * attributes. Angular's sanitizer still runs afterwards and stays the last line; this makes the
 * string correct before it gets there rather than relying on it to repair one.
 */
function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * PS-6495 — turn `{egroup_agb}Allgemeine Geschäftsbedingungen{/egroup_agb}` into an anchor.
 *
 * **Why placeholders rather than anchors in the text.** The sentences are legal text and will be
 * edited by translators in a database table. A URL is not a translation: embedded, the same
 * `egroup-technologies.com/agb` would sit in six rows per brand, five of them changing nothing
 * linguistically, and every one of them an `href` a translator can break on a screen that concludes
 * a contract. Separated, a translator moves a marker and cannot touch a target.
 *
 * **The house already does this**, with one difference. `CallbackOnCallNoteService:422-424` puts a
 * `:link` marker in the translated callback conditions and substitutes the brand's AGB URL into it.
 * Same idea; this takes the NAMED-pair form because one sentence here carries several links where that
 * one carries a single link, and because `:link` substitutes into an `href` the translator can
 * still see, which is the part worth removing. That substitution also happens in the backend; ours
 * is in the frontend because the URLs need a token and only exist after the sign-in.
 *
 * **A marker with no URL keeps its label as plain text.** That is the degradation agreed with
 * Torsten on 2026-10-09 for a brand whose rows have not been authored: the sentence stays whole and
 * readable, it just does not link. A document name pointed at a plausible-looking wrong page would
 * be a false statement about what the customer just accepted; an unlinked one is only a gap.
 *
 * `target="_blank"` because the customer is mid-order and following a link must not throw the run
 * away; `rel="noopener noreferrer"` so the new tab cannot reach back into this one and does not
 * carry the order screen's URL with it.
 */
export function renderLegalLinks(text: string, links: LegalLinks): string {
	return text.replace(PLACEHOLDER, (_match, key: string, label: string) => {
		const href = links[key as LegalLinkKey];
		if (!href) {
			return label;
		}
		return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
	});
}
